/**
 * Instant Upload Service
 *
 * Provides immediate file upload functionality for CSV files.
 * Files are uploaded directly to the backend as soon as user adds them,
 * with deduplication via SHA-256 hash check.
 */

import { getApiUrl } from '../../../config/environment';
import { getSession } from '../../../core/auth/supabase-auth';

export interface FileMetadata {
  bezeichnung?: string;
  type: 'input' | 'output';
  min?: string;
  max?: string;
  offset?: string;
  datenpunkte?: string;
  numerischeDatenpunkte?: string;
  numerischerAnteil?: string;
  datenform?: string;
  datenanpassung?: string;
  zeitschrittweite?: string;
  zeitschrittweiteAvgValue?: number;
  zeitschrittweiteMinValue?: number;
  skalierung?: string;
  skalierungMax?: string;
  skalierungMin?: string;
  zeithorizontStart?: string;
  zeithorizontEnd?: string;
  utcMin?: string;
  utcMax?: string;
  [key: string]: unknown;
}

export interface InstantUploadResult {
  success: boolean;
  fileId: string;
  isNew: boolean;
  storagePath: string;
  message?: string;
  error?: string;
}

/**
 * Calculate SHA-256 hash of file content
 */
async function calculateFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Upload a single file immediately to the backend.
 *
 * This function:
 * 1. Calculates file hash for deduplication
 * 2. Sends file + metadata to /instant-upload endpoint
 * 3. Backend checks if file already exists (by hash)
 * 4. Returns file ID (existing or new)
 *
 * @param file - The File object to upload
 * @param sessionId - Session ID (string format, not UUID)
 * @param metadata - File metadata (bezeichnung, type, etc.)
 * @returns Upload result with fileId, isNew flag, and storagePath
 */
export async function instantUploadFile(
  file: File,
  sessionId: string,
  metadata: FileMetadata
): Promise<InstantUploadResult> {
  // Get authentication token
  const session = await getSession();
  const accessToken = session?.access_token;

  if (!accessToken) {
    return {
      success: false,
      fileId: '',
      isNew: false,
      storagePath: '',
      error: 'No authentication token available. Please log in.'
    };
  }

  try {
    // Calculate file hash for deduplication
    const fileHash = await calculateFileHash(file);

    // Prepare form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sessionId', sessionId);
    formData.append('fileMetadata', JSON.stringify(metadata));
    formData.append('fileHash', fileHash);

    // Make request to instant-upload endpoint
    const url = getApiUrl('/api/training/instant-upload');
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
        // Note: Don't set Content-Type for FormData - browser sets it with boundary
      },
      body: formData
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        fileId: '',
        isNew: false,
        storagePath: '',
        error: data.error || data.message || 'Upload failed'
      };
    }

    return {
      success: data.success,
      fileId: data.data?.fileId || '',
      isNew: data.data?.isNew ?? true,
      storagePath: data.data?.storagePath || '',
      message: data.data?.message
    };

  } catch (error) {
    console.error('Instant upload error:', error);
    return {
      success: false,
      fileId: '',
      isNew: false,
      storagePath: '',
      error: error instanceof Error ? error.message : 'Unknown upload error'
    };
  }
}

/**
 * Check if a session exists, create one if not.
 * This is useful when user starts adding files before explicitly creating a session.
 *
 * @param currentSessionId - Current session ID (may be null/empty)
 * @returns Session ID (existing or newly created)
 */
export async function ensureSessionExists(
  currentSessionId: string | null
): Promise<string | null> {
  if (currentSessionId) {
    return currentSessionId;
  }

  // Session creation is handled elsewhere - this is a placeholder
  // The actual session creation happens in TrainingPage or similar
  return null;
}

/**
 * Batch check which files need uploading based on their hashes.
 * This is useful for bulk operations to minimize API calls.
 *
 * @param sessionId - Session ID
 * @param files - Array of files with their hashes
 * @returns Array of file indices that need uploading
 */
export async function checkFilesExist(
  _sessionId: string,
  files: Array<{ file: File; hash?: string }>
): Promise<number[]> {
  // For now, we return all indices as needing upload
  // The deduplication happens server-side per file
  // This could be optimized with a batch endpoint if needed
  return files.map((_, index) => index);
}

export const InstantUploadService = {
  uploadFile: instantUploadFile,
  calculateHash: calculateFileHash,
  ensureSessionExists,
  checkFilesExist
};

export default InstantUploadService;
